import { useEffect, useRef, useState } from "react";
import { money } from "../format";
import type { Listing } from "../../../shared/types";

const LEAFLET_VERSION = "1.9.4";
let leafletPromise: Promise<any> | null = null;

/** Leaflet is only worth downloading once someone opens a listing. */
function loadLeaflet(): Promise<any> {
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}/leaflet.css`;
    document.head.append(css);

    const script = document.createElement("script");
    script.src = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}/leaflet.js`;
    script.async = true;
    script.addEventListener("load", () => resolve((window as any).L));
    script.addEventListener("error", () => reject(new Error("Leaflet failed to load")));
    document.head.append(script);
  });

  return leafletPromise;
}

export function ListingMap({ listing }: { listing: Listing }) {
  const node = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let map: any;
    let cancelled = false;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !node.current) return;

        map = L.map(node.current, { scrollWheelZoom: false }).setView([listing.lat, listing.lng], 14);

        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        // A circle rather than a precise pin. The data is neighbourhood-level.
        L.circle([listing.lat, listing.lng], {
          radius: 350,
          color: "#146b58",
          weight: 2,
          fillColor: "#146b58",
          fillOpacity: 0.14,
        }).addTo(map);

        L.marker([listing.lat, listing.lng])
          .addTo(map)
          .bindPopup(`<strong>${escapeHtml(listing.title)}</strong><br>${escapeHtml(listing.neighborhood)} · ${money(listing.price)}/mo`);
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [listing.id, listing.lat, listing.lng, listing.title, listing.neighborhood, listing.price]);

  if (failed) {
    return (
      <p className="map-note">
        Map unavailable. {listing.neighborhood}, {listing.city}, {listing.lat.toFixed(4)}, {listing.lng.toFixed(4)}.
      </p>
    );
  }

  return (
    <>
      <div className="map" ref={node} />
      <p className="map-note">
        Approximate location. The pin marks the neighbourhood, not the exact address.
      </p>
    </>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);
}
